/// <amd-module name="Analytics/Shared/Common/AnalyticsDataValidator" />
import { AnalyticsExceptionType, extractODataError, recognizeAnalyticsException } from "Analytics/Common/Utilities/AnalyticsExceptionUtilities";
import * as Resources from "Analytics/Shared/Common/Resources";
import { ViewType } from "Analytics/Shared/Common/Types";
import { AnalyticsUnavailableMessage } from "Analytics/Shared/Visuals/Common/Components/AnalyticsUnavailableMessage";
import * as React from "react";
import { IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";

interface IAnalyticsDataValidationState {
    viewType?: ViewType;
    errorText?: string;
}

export interface IAnalyticsDataValidationProps extends IVssComponentProperties {
    dataValidationPromises: Promise<any>[] | undefined;
    compactView?: boolean;
    getMessage?: (viewType: ViewType | undefined, errorText?: string) => string | undefined;
    getSuggestion?: (viewType: ViewType | undefined, errorText?: string) => string | undefined;
}

export class AnalyticsDataValidator extends VssComponent<IAnalyticsDataValidationProps, IAnalyticsDataValidationState> {
    constructor(props: IAnalyticsDataValidationProps, context: IVssComponentContext) {
        super(props, context);
        this.state = {};
    }

    public render() {
        let view = <Spinner className="analytics-card-spinner flex-grow flex-column" size={SpinnerSize.large} />;
        let suggestion = this.props.getSuggestion ? this.props.getSuggestion(this.state.viewType, this.state.errorText) : undefined;
        let message = this.props.getMessage ? this.props.getMessage(this.state.viewType, this.state.errorText) : undefined;
        if (this.state) {
            if (this.state.viewType === ViewType.ReportView) {
                view = <>{this.props.children}</>;
            } else if (this.state.viewType === ViewType.DataNotReadyView) {
                message = message == null ? Resources.AnalyticsDataNotReadyMessage : message;
                suggestion = suggestion == null ? Resources.AnalyticsDataNotReadySuggestion : suggestion;
                view = (
                    <AnalyticsUnavailableMessage
                        imageName={!this.props.compactView ? "data-not-ready.svg" : ""}
                        iconName={this.props.compactView ? "ExploreData" : ""}
                        message={message}
                        suggestion={suggestion}
                        ariaDescription={message || suggestion}
                        messageClassName={this.props.compactView ? "ax-unavailable-error-message" : ""}
                    />
                );
            } else if (this.state.viewType === ViewType.ErrorView) {
                message = message == null ? Resources.AnalyticsErrorMessage : message;
                suggestion = suggestion == null ? this.state.errorText : suggestion;
                view = (
                    <AnalyticsUnavailableMessage
                        imageName={!this.props.compactView ? "ServiceError.svg" : ""}
                        message={message}
                        suggestion={suggestion}
                        ariaDescription={message || suggestion}
                    />
                );
            }
        }

        return view;
    }

    public async componentDidMount(): Promise<void> {
        super.componentDidMount();
        await this._validateData();
    }

    private async _validateData(): Promise<void> {
        if (!this.props.dataValidationPromises || this.props.dataValidationPromises.length == 0) {
            this.setState({ viewType: ViewType.ReportView });
            return Promise.resolve();
        } else {
            Promise.race(this.props.dataValidationPromises)
                .then(() => {
                    this.setState({ viewType: ViewType.ReportView });
                })
                .catch(error => {
                    if (recognizeAnalyticsException(error) === AnalyticsExceptionType.DataNotReady) {
                        this.setState({ viewType: ViewType.DataNotReadyView });
                    } else {
                        this.setState({
                            viewType: ViewType.ErrorView,
                            errorText: extractODataError(error)
                        });
                    }
                });
        }
    }
}
